// The collection dialog (`design-docs/Sidebar redesign — design.md` ▸ The collection dialog; the Mac's CollectionEditor):
// New Collection…, Edit Collection…, Save as Collection… and Save as New Collection… all come here. A tile in the
// collection's colour and icon, its name (an empty one saves as "Untitled collection"), the colours and icons offered,
// where it looks (Manuscript, Research, Everything), Any or All, and the rules: each a row with its facet's tile and
// name, the condition as a button that turns it round, and the value — a list of the options with their dots, a field
// for words, else the facet's own editor in a popover. Add Rule offers the facets in groups. The foot says how much
// matches now. Saved as JSON in the project's settings (`PlanEditor.putSavedSearch`), so it syncs; members are never
// stored.
//
// It's a modal `<dialog>` (the top layer), so its popovers are drawn inside it, never in a portal.
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { entityKindOf, type OpenedProject } from '@writing-suite/core';
import {
  COLLECTION_ICON_CHOICES, COLLECTION_INKS, collectionIcon, collectionInk, newSavedSearch, type CollectionIcon, type SavedSearch, type SavedSearchRule,
} from '@writing-suite/core/plan';
import {
  documentNoun, facetLabel, facetParts, facetStyle, nounCount, isUnfilled, KIND_CHOICES, ruleChoices, ruleCondition, SCOPE_NAMES, sceneKind,
} from '../../store/binderFilter.ts';
import { membersOf, projectTags } from '../../store/planModel.ts';
import { cssColor, formatNumber, pathTo } from '../helpers.ts';
import { FacetEditor } from '../FacetEditor.tsx';
import { Icon, Tile, type IconName, type Ink } from '../Icons.tsx';
import { Dot } from '../Menu.tsx';

const LOOK_IN: { scope: SavedSearch['scope']; icon: IconName }[] = [
  { scope: 'manuscript', icon: 'book' },
  { scope: 'research', icon: 'notes' },
  { scope: 'all-text', icon: 'all' },
];

/** A value offered in a rule's list. */
interface Choice {
  id: string;
  name: string;
  color?: string | null;
}

/** How a rule's value is chosen: from a list (`single`: one only), as words, in the facet's own editor, or not at all. */
type ValueControl = { kind: 'pick'; choices: Choice[]; single?: boolean } | { kind: 'words'; placeholder: string } | { kind: 'editor' } | { kind: 'none' };

function valueControl(rule: SavedSearchRule, project: OpenedProject, tags: readonly { tag: string; count: number }[]): ValueControl {
  const entities = (kinds: string[] | null): Choice[] =>
    project.record.entities.filter((e) => !kinds || kinds.includes(e.kind)).sort((a, b) => a.name.localeCompare(b.name)).map((e) => ({ id: e.id, name: e.name, color: cssColor(e.color) }));
  switch (rule.kind) {
    case 'field': {
      const field = project.allFields().find((f) => f.id === rule.field);
      if (rule.op === 'contains') return { kind: 'words', placeholder: 'A word or phrase' };
      if (rule.op === 'empty') return { kind: 'none' };
      if (field?.type === 'enum') return { kind: 'pick', choices: [...field.options.map((o) => ({ id: o.id, name: o.name, color: cssColor(o.color) })), { id: '', name: 'None' }] };
      if (field?.type === 'entity-ref') {
        const kind = field.entityKind ?? entityKindOf(field.id);
        return { kind: 'pick', choices: [...entities(kind ? [kind] : null), { id: '', name: 'None' }] };
      }
      if (field?.type === 'tags') return { kind: 'pick', choices: [...tags.map((t) => ({ id: t.tag, name: t.tag })), { id: '', name: 'Untagged' }] };
      if (field?.type === 'boolean') return { kind: 'pick', single: true, choices: [{ id: 'true', name: 'Yes' }, { id: 'false', name: 'No' }] };
      return { kind: 'editor' };
    }
    case 'entity':
      return { kind: 'pick', choices: entities(rule.level === 'pov' ? ['character'] : null) };
    case 'text': case 'title': case 'synopsis': case 'notes':
      return rule.op === 'empty' ? { kind: 'none' } : { kind: 'words', placeholder: rule.kind === 'title' ? 'Part of a title' : 'A word or phrase, e.g. fog' };
    case 'kind':
      return { kind: 'pick', choices: KIND_CHOICES.map((k) => ({ id: k.id === 'prose' ? sceneKind(project) : k.id, name: k.name })) };
    case 'target':
      return { kind: 'pick', single: true, choices: [{ id: 'under', name: 'Under target' }, { id: 'reached', name: 'Reached' }] };
    case 'words': case 'modified-within': case 'comments':
      return { kind: 'editor' };
    default:
      return { kind: 'none' };
  }
}

/** A value with nothing to choose, in words ("Included", a folder's title); empty when the condition says it all. */
function staticValue(rule: SavedSearchRule, title: (id: string) => string | null): string {
  switch (rule.kind) {
    case 'field': return rule.op === 'empty' ? 'empty' : '';
    case 'included-in-compile': return 'Included';
    case 'locked': return 'Locked';
    case 'suggestions': return 'Open suggestions';
    case 'within': return rule.values.map((id) => title(id) ?? 'a folder').join(' or ');
    case 'unsupported': return 'Made in a newer version';
    default: return '';
  }
}

export function CollectionEditor({ project, search, draft, onSave, onCancel }: {
  project: OpenedProject;
  search: SavedSearch | null;
  draft?: Partial<SavedSearch>;
  onSave: (s: SavedSearch) => void;
  onCancel: () => void;
}) {
  const [s, setS] = useState<SavedSearch>(() => search ?? newSavedSearch({ name: '', ...draft, rules: draft?.rules ?? [] }));
  const [ink, setInk] = useState<Ink>(() => collectionInk(s) as Ink);
  const [icon, setIcon] = useState<CollectionIcon>(() => collectionIcon(s));
  // The popover open: a rule's value list or editor, or Add Rule's panel.
  const [pop, setPop] = useState<{ rule: string; what: 'pick' | 'editor' } | 'add' | null>(null);
  const tags = useMemo(() => projectTags(project), [project]);
  const groups = useMemo(() => ruleChoices(project, tags.length > 0), [project, tags.length]);
  const filled = useMemo(() => ({ ...s, rules: s.rules.filter((r) => !isUnfilled(r)) }), [s]);
  const members = useMemo(() => membersOf(project, filled), [project, filled]);
  const words = useMemo(() => members.reduce((n, id) => n + project.words(id), 0), [project, members]);
  const fields = project.allFields();
  const titleId = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const addButton = useRef<HTMLButtonElement>(null);
  // Add Rule's panel opens upwards when the rules above leave it room (as the design has it), else downwards.
  const [addRoom, setAddRoom] = useState<number | null>(null);

  useEffect(() => {
    const d = dialog.current;
    if (d && !d.open) d.showModal();
    return () => d?.close();
  }, []);
  // A press outside the popover closes it (the button that opened it toggles it).
  useEffect(() => {
    if (!pop) return;
    const down = (e: PointerEvent) => {
      const t = e.target as HTMLElement;
      if (popRef.current?.contains(t) || t.closest('[data-pop-opener]')) return;
      setPop(null);
    };
    document.addEventListener('pointerdown', down, true);
    if (pop === 'add' && addRoom !== null) popRef.current?.style.setProperty('max-height', `${addRoom}px`);
    else popRef.current?.scrollIntoView?.({ block: 'nearest' });
    return () => document.removeEventListener('pointerdown', down, true);
  }, [pop, addRoom]);

  const setRule = (rule: SavedSearchRule) => setS((x) => ({ ...x, rules: x.rules.map((r) => (r.id === rule.id ? rule : r)) }));
  const removeRule = (id: string) => {
    setS((x) => ({ ...x, rules: x.rules.filter((r) => r.id !== id) }));
    setPop(null);
  };
  const noun = documentNoun(project.type, s.scope === 'manuscript');
  const nodeTitle = (id: string) => {
    for (const a of project.arrangements()) {
      const n = pathTo(project.tree(a.id), id)?.at(-1);
      if (n) return n.title || 'Untitled';
    }
    return null;
  };
  const save = () =>
    onSave({ ...s, name: s.name.trim() || 'Untitled collection', color: ink, icon, rules: s.rules.filter((r) => !isUnfilled(r)) });

  return (
    <dialog
      ref={dialog}
      className={`collection-dialog tint-${ink}`}
      aria-label={search ? 'Edit Collection' : 'New Collection'}
      onCancel={(e) => {
        e.preventDefault();
        if (pop) setPop(null);
        else onCancel();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && pop) {
          e.preventDefault();
          e.stopPropagation();
          setPop(null);
        }
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <header className="collection-head">
          <div className="collection-title">
            <Tile icon={icon} ink={ink} size={46} />
            <div className="collection-title-text">
              <p className="collection-eyebrow" id={titleId}>{search ? 'Edit collection' : 'New collection'}</p>
              <input
                type="text"
                className="collection-name"
                aria-label="Name"
                placeholder="Name this collection"
                value={s.name}
                autoFocus
                onChange={(e) => setS({ ...s, name: e.target.value })}
              />
            </div>
            <button type="button" className="card-button" aria-label="Close" onClick={onCancel}>
              <Icon name="close" size={15} />
            </button>
          </div>
          <div className="collection-looks">
            <div className="collection-swatches" role="radiogroup" aria-label="Colour">
              {COLLECTION_INKS.map((c) => (
                <button key={c} type="button" role="radio" aria-checked={ink === c} aria-label={c[0].toUpperCase() + c.slice(1)} className={`swatch tint-${c}`} onClick={() => setInk(c)} />
              ))}
            </div>
            <span className="collection-looks-rule" aria-hidden="true" />
            <div className="collection-icons" role="radiogroup" aria-label="Icon">
              {COLLECTION_ICON_CHOICES.map((c) => (
                <button key={c} type="button" role="radio" aria-checked={icon === c} aria-label={c[0].toUpperCase() + c.slice(1)} className="icon-choice" onClick={() => setIcon(icon === c ? 'stack' : c)}>
                  <Icon name={c} size={15} />
                </button>
              ))}
            </div>
          </div>
        </header>

        <div className="collection-body" ref={body}>
          <div className="collection-look-in">
            <span id={`${titleId}-look`}>Look in</span>
            <div className="collection-segmented" role="radiogroup" aria-labelledby={`${titleId}-look`}>
              {LOOK_IN.map((l) => (
                <button
                  key={l.scope}
                  type="button"
                  role="radio"
                  aria-checked={s.scope === l.scope}
                  onClick={() => setS({ ...s, scope: l.scope, unsupportedScope: undefined })}
                >
                  <Icon name={l.icon} size={13} />
                  {SCOPE_NAMES[l.scope]}
                </button>
              ))}
            </div>
          </div>

          <div className="collection-match">
            <span>Include {noun}s that match</span>
            <div className="collection-segmented any-all" role="radiogroup" aria-label="Match">
              {(['any', 'all'] as const).map((m) => (
                <button key={m} type="button" role="radio" aria-checked={(m === 'all') === s.matchAll} onClick={() => setS({ ...s, matchAll: m === 'all' })}>
                  {m === 'all' ? 'All' : 'Any'}
                </button>
              ))}
            </div>
            <span>of these rules</span>
          </div>

          {s.rules.length ? (
            <ol className="collection-rules" aria-label="Rules">
              {s.rules.map((r, i) => {
                const style = facetStyle(r, project);
                const condition = ruleCondition(r, fields);
                const control = valueControl(r, project, tags);
                const parts = facetParts(r, project, nodeTitle);
                const open = pop !== null && pop !== 'add' && pop.rule === r.id ? pop.what : null;
                const unfilled = isUnfilled(r);
                return [
                  i > 0 ? <li key={`${r.id}-join`} className="rule-join" aria-hidden="true">{s.matchAll ? 'AND' : 'OR'}</li> : null,
                  <li key={r.id} className={`collection-rule${open ? ' open' : ''}`} data-rule-kind={r.kind} aria-label={`Rule ${i + 1}: ${facetLabel(r, project, nodeTitle)}`}>
                    <span className="rule-facet">
                      <Tile icon={style.icon} ink={style.ink} size={26} />
                      <span className="rule-facet-name">{style.name}</span>
                    </span>
                    {condition ? (
                      <button type="button" className="rule-condition" aria-label={`Condition: ${condition.label}`} title="Change the condition" onClick={() => setRule(condition.next)}>
                        {condition.label}
                      </button>
                    ) : null}
                    <div className="rule-value">
                      {control.kind === 'words' ? (
                        <input
                          type="text"
                          aria-label="Words"
                          placeholder={control.placeholder}
                          value={r.values[0] ?? ''}
                          onChange={(e) => setRule({ ...r, values: e.target.value ? [e.target.value] : [] })}
                        />
                      ) : control.kind === 'pick' || control.kind === 'editor' ? (
                        <button
                          type="button"
                          className={`rule-value-button${unfilled ? ' empty' : ''}`}
                          aria-label={`Value: ${unfilled ? 'Choose…' : parts.value}`}
                          aria-haspopup={control.kind === 'pick' ? 'listbox' : 'dialog'}
                          aria-expanded={!!open}
                          data-pop-opener
                          onClick={() => setPop(open ? null : { rule: r.id, what: control.kind === 'pick' ? 'pick' : 'editor' })}
                        >
                          {parts.dot ? <Dot color={parts.dot} className="rule-dot" /> : null}
                          <span className="rule-value-text">{unfilled ? 'Choose…' : parts.value}</span>
                          <Icon name="chevron" size={13} />
                        </button>
                      ) : (
                        <span className="rule-value-static">{staticValue(r, nodeTitle)}</span>
                      )}
                      {open === 'pick' && control.kind === 'pick' ? (
                        <div className="collection-pop rule-options" ref={popRef} role="listbox" aria-label={style.name} aria-multiselectable={!control.single}>
                          {control.choices.map((c) => {
                            const on = r.values.includes(c.id);
                            return (
                              <div
                                key={c.id}
                                role="option"
                                tabIndex={0}
                                aria-selected={on}
                                className="rule-option"
                                onClick={() => {
                                  if (control.single) {
                                    setRule({ ...r, values: [c.id] });
                                    setPop(null);
                                  } else setRule({ ...r, values: on ? r.values.filter((v) => v !== c.id) : [...r.values, c.id] });
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    (e.currentTarget as HTMLElement).click();
                                  } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    const el = e.currentTarget as HTMLElement;
                                    ((e.key === 'ArrowDown' ? el.nextElementSibling : el.previousElementSibling) as HTMLElement | null)?.focus();
                                  }
                                }}
                              >
                                <Dot color={c.color ?? null} className="rule-dot" />
                                <span className="rule-option-name">{c.name}</span>
                                <span className="rule-option-check" aria-hidden="true">{on ? <Icon name="check" size={13} /> : null}</span>
                              </div>
                            );
                          })}
                          {control.choices.length === 0 ? <p className="facet-hint">Nothing to choose yet.</p> : null}
                        </div>
                      ) : null}
                      {open === 'editor' ? (
                        <div className="collection-pop rule-editor" ref={popRef}>
                          <FacetEditor
                            rule={r}
                            project={project}
                            tags={tags}
                            label={facetLabel(r, project, nodeTitle)}
                            id={`rule-editor-${r.id}`}
                            onChange={setRule}
                            onRemove={() => removeRule(r.id)}
                            onDone={() => setPop(null)}
                          />
                        </div>
                      ) : null}
                    </div>
                    <button type="button" className="card-button rule-remove" aria-label={`Remove rule ${i + 1}`} onClick={() => removeRule(r.id)}>
                      <Icon name="trash" size={14} />
                    </button>
                  </li>,
                ];
              })}
            </ol>
          ) : (
            <p className="collection-empty">
              With no rules, every document in {s.scope === 'manuscript' ? 'the manuscript' : s.scope === 'research' ? 'Research & Notes' : 'the project'} is included.
            </p>
          )}

          <div className="collection-add">
            <button
              ref={addButton}
              type="button"
              className="collection-add-button"
              aria-haspopup="menu"
              aria-expanded={pop === 'add'}
              data-pop-opener
              onClick={() => {
                const b = addButton.current?.getBoundingClientRect();
                const t = body.current?.getBoundingClientRect();
                const room = b && t ? Math.floor(b.top - t.top - 12) : 0;
                setAddRoom(room >= 260 ? Math.min(room, 420) : null);
                setPop(pop === 'add' ? null : 'add');
              }}
            >
              <Icon name="plus" size={14} />
              Add rule
            </button>
            {pop === 'add' ? (
              <div className={`collection-pop add-rule${addRoom !== null ? ' up' : ''}`} ref={popRef} role="menu" aria-label="Add rule">
                {groups.map((g) => (
                  <div key={g.title} role="group" aria-label={g.title}>
                    <p className="add-rule-group" aria-hidden="true">{g.title}</p>
                    {g.items.map((item) => {
                      const sample = facetStyle(item.make(), project);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          role="menuitem"
                          className="add-rule-item"
                          aria-label={item.name}
                          onClick={() => {
                            const rule = item.make();
                            setS((x) => ({ ...x, rules: [...x.rules, rule] }));
                            const next = valueControl(rule, project, tags);
                            setPop(next.kind === 'pick' ? { rule: rule.id, what: 'pick' } : next.kind === 'editor' ? { rule: rule.id, what: 'editor' } : null);
                          }}
                        >
                          <Tile icon={sample.icon} ink={sample.ink} size={28} />
                          <span className="add-rule-text">
                            <span className="add-rule-name">{item.name}</span>
                            <span className="add-rule-description">{item.description}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <p className="collection-note">Compile presets can use this collection by name.</p>
        </div>

        <footer className="collection-foot">
          <p className={`collection-preview${members.length ? ' some' : ''}`} role="status" data-count={members.length}>
            <span className="collection-preview-dot" aria-hidden="true" />
            {members.length ? (
              <>
                <span className="collection-preview-count">
                  {nounCount(noun, members.length)} · {formatNumber(words)} {words === 1 ? 'word' : 'words'}
                </span>
                <span className="collection-preview-sub">match now</span>
              </>
            ) : (
              <span className="collection-preview-count">Nothing matches yet</span>
            )}
          </p>
          <button type="button" className="button secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="button collection-save">
            Save collection
          </button>
        </footer>
      </form>
    </dialog>
  );
}
